import * as React from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Button } from '../../core/button';
import { Input } from '../../core/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../core/popover';
import { Switch } from '../../core/switch';
import { Label } from '../../core/label';
import { cn } from '../../../utils';
import {
  FILTER_ALL,
  countActiveFilters,
  type FilterDef,
  type FilterOption,
  type FilterValue,
  type FilterValues,
} from './use-list-filters';

type FilterPopoverProps = {
  /** Sections, in the order they are shown (and the order of the chips). */
  filters: FilterDef[];
  values: FilterValues;
  /** Applied immediately on every click — there is no "Apply". */
  onChange: (key: string, value: FilterValue) => void;
  onClear: () => void;
  /** Trigger text, e.g. "Filtros". */
  label: string;
  clearLabel: string;
  /** "No choice" wording: the collapsed header value and the chip-grid reset. */
  allLabel?: string;
  /** Placeholder of the search inside an expanded long list. */
  searchPlaceholder?: string;
  /** "{{count}} selecionados" for a multi list header; `{{count}}` is replaced. */
  selectedLabel?: string;
  /** Option count above which a section collapses into a searchable list. */
  listThreshold?: number;
  /** Chips shown before the dashed "+N" chip. */
  chipLimit?: number;
  /** Show option counts on chips too (lists always show them). */
  countsOnChips?: boolean;
  className?: string;
};

/** Up to this many options a section is a chip grid; above, a collapsed searchable list. */
export const FILTER_LIST_THRESHOLD = 12;
/** Chip grids show this many chips before the dashed "+N". */
export const FILTER_CHIP_LIMIT = 8;

type Display = 'chips' | 'list';

/** Presentation of an option section: explicit `display` wins, else the option count decides. */
export function presentationOf(def: FilterDef, listThreshold = FILTER_LIST_THRESHOLD): Display | 'toggle' {
  if (def.type === 'toggle') return 'toggle';
  if (def.display) return def.display;
  return def.options.length > listThreshold ? 'list' : 'chips';
}

/** Options with a count of 0 are hidden unless selected; order is kept. */
export function visibleOptions(options: FilterOption[], selected: readonly string[]): FilterOption[] {
  return options.filter((o) => o.count !== 0 || selected.includes(o.value));
}

/**
 * The first `limit` chips, but every selected option is always among them
 * (a selected chip past the limit takes the place of the last unselected one).
 */
export function chipsToShow(options: FilterOption[], selected: readonly string[], limit: number): { shown: FilterOption[]; hidden: number } {
  if (options.length <= limit) return { shown: options, hidden: 0 };
  const chosen = options.filter((o) => selected.includes(o.value));
  const rest = options.filter((o) => !selected.includes(o.value));
  const room = Math.max(0, limit - chosen.length);
  const shown = options.filter((o) => selected.includes(o.value) || rest.slice(0, room).includes(o));
  return { shown, hidden: options.length - shown.length };
}

/** Selected values of a section, whatever its type. */
export function selectedOf(def: FilterDef, value: FilterValue): string[] {
  if (def.type === 'select') return typeof value === 'string' && value !== '' && value !== FILTER_ALL ? [value] : [];
  if (def.type === 'multi') return Array.isArray(value) ? value : [];
  return [];
}

/**
 * One button that opens every filter as a FLAT panel, one section per
 * filter in the order given. Short option lists (≤ `listThreshold`) are
 * chip grids you tap; long ones are collapsed rows showing the current
 * value that expand inline into a search box plus a scrollable list with
 * counts. Toggles are switches. No nested Select/DropdownMenu, so option
 * order is exactly the order given; choices apply on click.
 */
export function FilterPopover({
  filters,
  values,
  onChange,
  onClear,
  label,
  clearLabel,
  allLabel = 'All',
  searchPlaceholder = 'Search…',
  selectedLabel = '{{count}} selected',
  listThreshold = FILTER_LIST_THRESHOLD,
  chipLimit = FILTER_CHIP_LIMIT,
  countsOnChips = false,
  className,
}: FilterPopoverProps) {
  const active = countActiveFilters(filters, values);
  // Only one collapsed list open at a time.
  const [openKey, setOpenKey] = React.useState<string | null>(null);
  return (
    <Popover modal>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn('gap-2', className)} aria-label={label}>
          <SlidersHorizontal className="h-4 w-4" />
          <span>{label}</span>
          {active > 0 ? (
            <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground tabular-nums">
              {active}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" aria-label={label}>
        <div className="max-h-[70vh] overflow-y-auto p-3">
          <div className="space-y-4">
            {filters.map((def) => (
              <FilterSection
                key={def.key}
                def={def}
                value={values[def.key]}
                onChange={(v) => onChange(def.key, v)}
                allLabel={allLabel}
                searchPlaceholder={searchPlaceholder}
                selectedLabel={selectedLabel}
                display={presentationOf(def, listThreshold)}
                chipLimit={chipLimit}
                countsOnChips={countsOnChips}
                open={openKey === def.key}
                onOpenChange={(next) => setOpenKey(next ? def.key : null)}
              />
            ))}
          </div>
        </div>
        <div className="sticky bottom-0 flex items-center border-t bg-popover p-2">
          <Button variant="ghost" size="sm" onClick={onClear} disabled={active === 0}>
            {clearLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

type SectionProps = {
  def: FilterDef;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  allLabel: string;
  searchPlaceholder: string;
  selectedLabel: string;
  display: Display | 'toggle';
  chipLimit: number;
  countsOnChips: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function FilterSection(props: SectionProps) {
  const { def, value, onChange, display } = props;

  if (def.type === 'toggle' || display === 'toggle') {
    const id = `filter-${def.key}`;
    return (
      <section aria-labelledby={`${id}-label`} className="flex items-center justify-between gap-3">
        <Label id={`${id}-label`} htmlFor={id} className="text-sm font-normal">
          {def.type === 'toggle' ? def.onLabel ?? def.label : def.label}
        </Label>
        <Switch id={id} checked={value === true} onCheckedChange={(v) => onChange(v)} />
      </section>
    );
  }

  const selected = selectedOf(def, value);
  const options = visibleOptions(def.options, selected);
  const pick = (optionValue: string) => {
    if (def.type === 'select') {
      onChange(selected[0] === optionValue ? FILTER_ALL : optionValue);
    } else {
      onChange(selected.includes(optionValue) ? selected.filter((v) => v !== optionValue) : [...selected, optionValue]);
    }
  };

  if (display === 'list') {
    return <ListSection {...props} selected={selected} options={options} pick={pick} />;
  }
  return <ChipSection {...props} selected={selected} options={options} pick={pick} />;
}

type OptionSectionProps = SectionProps & { selected: string[]; options: FilterOption[]; pick: (value: string) => void };

function ChipSection({ def, selected, options, pick, chipLimit, countsOnChips, allLabel }: OptionSectionProps) {
  const [expanded, setExpanded] = React.useState(false);
  const headingId = `filter-${def.key}-label`;
  const { shown, hidden } = expanded ? { shown: options, hidden: 0 } : chipsToShow(options, selected, chipLimit);
  const gridRef = React.useRef<HTMLDivElement>(null);

  // Arrow keys move between the chips of one grid.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
    const chips = Array.from(gridRef.current?.querySelectorAll<HTMLButtonElement>('button[data-chip]') ?? []);
    const index = chips.indexOf(document.activeElement as HTMLButtonElement);
    if (index < 0) return;
    e.preventDefault();
    const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    chips[(index + (forward ? 1 : chips.length - 1)) % chips.length]?.focus();
  };

  return (
    <section aria-labelledby={headingId} className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div id={headingId} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {def.label}
        </div>
        {selected.length > 0 ? (
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => (def.type === 'select' ? pick(selected[0]) : selected.forEach(pick))}
          >
            {def.type === 'select' ? def.allLabel ?? allLabel : allLabel}
          </button>
        ) : null}
      </div>
      <div ref={gridRef} role="group" aria-labelledby={headingId} className="flex flex-wrap gap-1.5" onKeyDown={onKeyDown}>
        {shown.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              data-chip
              aria-pressed={on}
              onClick={() => pick(o.value)}
              className={cn(
                'inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                on ? 'border-foreground bg-foreground text-background' : 'border-border bg-background text-foreground hover:bg-muted',
              )}
            >
              <span>{o.label}</span>
              {countsOnChips && o.count !== undefined ? (
                <span className={cn('tabular-nums', on ? 'text-background/70' : 'text-muted-foreground')}>{o.count}</span>
              ) : null}
            </button>
          );
        })}
        {hidden > 0 ? (
          <button
            type="button"
            data-chip
            onClick={() => setExpanded(true)}
            aria-label={`+${hidden}`}
            className="inline-flex h-7 items-center rounded-full border border-dashed border-border px-2.5 text-xs text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            +{hidden}
          </button>
        ) : null}
        {options.length === 0 ? <div className="px-1 py-1 text-xs text-muted-foreground">—</div> : null}
      </div>
    </section>
  );
}

function ListSection({ def, selected, options, pick, open, onOpenChange, allLabel, searchPlaceholder, selectedLabel }: OptionSectionProps) {
  const [query, setQuery] = React.useState('');
  const headingId = `filter-${def.key}-label`;
  const panelId = `filter-${def.key}-panel`;
  const visible = filterOptions(options, query);
  const current =
    selected.length === 0
      ? def.type === 'select'
        ? def.allLabel ?? allLabel
        : allLabel
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label ?? selected[0]
        : selectedLabel.replace('{{count}}', String(selected.length));

  return (
    <section aria-labelledby={headingId} className="space-y-1.5">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => onOpenChange(!open)}
        className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-1 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span id={headingId} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {def.label}
        </span>
        <span className="flex min-w-0 items-center gap-1 text-sm">
          <span className={cn('truncate', selected.length > 0 ? 'font-medium' : 'text-muted-foreground')}>{current}</span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden />
        </span>
      </button>
      {open ? (
        <div id={panelId} className="space-y-1.5">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={`${def.label}: ${searchPlaceholder}`}
            className="h-8 text-sm"
            autoFocus
          />
          <div role="listbox" aria-labelledby={headingId} aria-multiselectable={def.type === 'multi'} className="max-h-56 overflow-y-auto pr-1">
            {visible.map((o) => {
              const on = selected.includes(o.value);
              return (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={on}
                  tabIndex={0}
                  onClick={() => pick(o.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      pick(o.value);
                    }
                  }}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    on && 'bg-muted font-medium',
                  )}
                >
                  <span className="truncate">{o.label}</span>
                  {o.count !== undefined ? <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{o.count}</span> : null}
                </div>
              );
            })}
            {visible.length === 0 ? <div className="px-1 py-2 text-xs text-muted-foreground">—</div> : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** Case/accent-insensitive "contains" on the label; keeps the given order. */
export function filterOptions(options: FilterOption[], query: string): FilterOption[] {
  const needle = normalize(query);
  if (!needle) return options;
  return options.filter((o) => normalize(o.label).includes(needle));
}

function normalize(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase();
}
